import { Button } from '@/components/Button'
import { Form, FormActions, FormGroup, FormRow, Help } from '@/components/Form'
import { NumericInput } from '@/components/inputs/NumericInput'
import {
  PRO_SPECIALTY_VALUES,
  SOFTWARE_VALUES,
  STATE_VALUES,
  SUPPLEMENTAL_SOFTWARE_VALUES,
} from '@/features/iam/providers/well-known-provider-values'
import {
  MultiStringSelect,
  SingleStringSelect,
} from '@/features/iam/providers/components/StringSelect'

export interface ProDetailsProps {
  softwareOwned: string[]
  onSoftwareOwnedChange: (value: string[]) => void
  softwareExperience: string[]
  onSoftwareExperienceChange: (value: string[]) => void
  supplementalSoftware: string[]
  onSupplementalSoftwareChange: (value: string[]) => void
  specialties: string[]
  onSpecialtiesChange: (value: string[]) => void
  stateRegion: string | undefined
  onStateRegionChange: (value: string | undefined) => void
  yearsExperience: number
  onYearsExperienceChange: (value: number | null) => void
  yearsExperienceError?: string
  editable: boolean
  showActions?: boolean
  onSubmit: () => unknown
  isSavePending: boolean
  canSave: boolean
}

export function ProDetails({
  softwareOwned,
  onSoftwareOwnedChange,
  softwareExperience,
  onSoftwareExperienceChange,
  supplementalSoftware,
  onSupplementalSoftwareChange,
  specialties,
  onSpecialtiesChange,
  stateRegion,
  onStateRegionChange,
  yearsExperience,
  onYearsExperienceChange,
  yearsExperienceError,
  editable,
  showActions = true,
  onSubmit,
  isSavePending,
  canSave,
}: ProDetailsProps) {
  const testId = 'pro-details'
  const disabled = !editable
  return (
    <Form onSubmit={onSubmit}>
      <FormRow>
        <FormGroup label="Software Owned">
          <MultiStringSelect
            value={softwareOwned}
            options={SOFTWARE_VALUES}
            onChange={onSoftwareOwnedChange}
            placeholder="Select software"
            isDisabled={disabled}
            testId={`${testId}-software-owned`}
          />
        </FormGroup>
      </FormRow>
      <FormRow>
        <FormGroup label="Software Experience">
          <MultiStringSelect
            value={softwareExperience}
            options={SOFTWARE_VALUES}
            onChange={onSoftwareExperienceChange}
            placeholder="Select software"
            isDisabled={disabled}
            testId={`${testId}-software-experience`}
          />
        </FormGroup>
      </FormRow>
      <FormRow>
        <FormGroup label="Supplemental Software">
          <MultiStringSelect
            value={supplementalSoftware}
            options={SUPPLEMENTAL_SOFTWARE_VALUES}
            onChange={onSupplementalSoftwareChange}
            placeholder="Select software"
            isDisabled={disabled}
            testId={`${testId}-supplemental-software`}
          />
        </FormGroup>
      </FormRow>
      <FormRow>
        <FormGroup label="Pro Specialties">
          <MultiStringSelect
            value={specialties}
            options={PRO_SPECIALTY_VALUES}
            onChange={onSpecialtiesChange}
            placeholder="Select specialties"
            isDisabled={disabled}
            testId={`${testId}-specialties`}
          />
        </FormGroup>
      </FormRow>
      <FormRow>
        <FormGroup label="Location">
          <SingleStringSelect
            value={stateRegion}
            options={STATE_VALUES}
            onChange={onStateRegionChange}
            placeholder="Select a state"
            isDisabled={disabled}
            testId={`${testId}-location`}
          />
        </FormGroup>
      </FormRow>
      <FormRow>
        <FormGroup label="Years of Experience">
          <NumericInput
            min={0}
            step={1}
            precision={0}
            value={yearsExperience}
            isDisabled={disabled}
            onChange={onYearsExperienceChange}
          />
          <Help error={yearsExperienceError} />
        </FormGroup>
      </FormRow>
      {editable && showActions && (
        <FormActions>
          <Button
            label="Save"
            type="submit"
            variant="primary"
            isPending={isSavePending}
            isDisabled={!canSave}
          />
        </FormActions>
      )}
    </Form>
  )
}
